import { Link } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { ErrorState, buttonVariantClasses, cx } from "@wato/ui";
import { isApiError } from "../api/client";

type Kind =
  | "plan"
  | "moduleOff"
  | "forbidden"
  | "rateLimited"
  | "offline"
  | "server"
  | "notFound"
  | "generic";

export function classifyError(error: unknown): Kind {
  if (!isApiError(error)) return "generic";
  if (error.status === 0) return "offline";
  if (error.code === "RATE_LIMITED" || error.status === 429) return "rateLimited";
  if (/ENTITLEMENT|FEATURE_NOT|PLAN_/.test(error.code) || error.status === 402) return "plan";
  // OWN-153: a module switched off in Settings → Features is not a permission problem.
  if (error.code === "MODULE_DISABLED") return "moduleOff";
  if (error.status === 403) return "forbidden";
  if (error.status === 404) return "notFound";
  if (error.status >= 500) return "server";
  return "generic";
}

/**
 * UX-23: one place that turns a failed API call into an actionable message — plan gate →
 * Upgrade, permission → "ask your admin", rate limit → wait time, offline, 5xx → support
 * reference (`x-request-id`) — always with Retry when `onRetry` is given.
 */
export function ApiErrorState({
  error,
  onRetry,
  workspaceId,
}: {
  error: unknown;
  onRetry?: () => void;
  workspaceId?: string | undefined;
}) {
  const { t } = useI18n();
  const kind = classifyError(error);
  const key = (part: string) => `dashboard.errors.${part}` as MessageKey;
  const api = isApiError(error) ? error : null;
  const role = [api?.details.requiredRole, api?.details.role].find(
    (v): v is string => typeof v === "string" && v !== "",
  );
  const message = (() => {
    switch (kind) {
      case "plan":
        return t(key("notInPlanBody"));
      case "moduleOff":
        return t(key("moduleOffBody"));
      case "forbidden":
        return role ? t(key("forbiddenRole"), { role }) : t(key("forbiddenBody"));
      case "rateLimited":
        return api?.retryAfterSeconds
          ? t(key("rateLimitedBody"), { seconds: api.retryAfterSeconds })
          : t(key("rateLimitedBodyNoWait"));
      case "offline":
        return t(key("offlineBody"));
      case "server":
        return t(key("serverBody"));
      case "notFound":
        return t(key("notFoundBody"));
      default:
        return t("dashboard.auth.genericError");
    }
  })();
  const title = t(
    key(
      {
        plan: "notInPlanTitle",
        moduleOff: "moduleOffTitle",
        forbidden: "forbiddenTitle",
        rateLimited: "rateLimitedTitle",
        offline: "offlineTitle",
        server: "serverTitle",
        notFound: "notFoundTitle",
        generic: "genericTitle",
      }[kind],
    ),
  );
  return (
    <ErrorState
      tone={kind === "plan" || kind === "moduleOff" || kind === "offline" ? "warning" : "error"}
      title={title}
      message={message}
      {...(api?.requestId ? { reference: t(key("requestId"), { id: api.requestId }) } : {})}
      {...(onRetry ? { onRetry, retryLabel: t(key("retry")) } : {})}
      action={
        kind === "plan" && workspaceId ? (
          <Link
            to={`/w/${workspaceId}/settings/billing`}
            className={cx(
              "inline-flex h-8 items-center rounded-md px-3 text-sm font-medium",
              buttonVariantClasses.primary,
            )}
          >
            {t(key("upgrade"))}
          </Link>
        ) : kind === "moduleOff" && workspaceId ? (
          <Link
            to={`/w/${workspaceId}/settings/features`}
            className={cx(
              "inline-flex h-8 items-center rounded-md px-3 text-sm font-medium",
              buttonVariantClasses.primary,
            )}
          >
            {t(key("moduleOffAction"))}
          </Link>
        ) : undefined
      }
    />
  );
}
